import { Box, Grid, Radio, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { FieldIcon } from '/@/components/field-info-v2/field-card';
import { useUnitProvider } from '/@/core/context/unit';
import type { LastCampaign } from '/@/hooks/crop-monitoring/use-last-campaign';
import { useGetUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import type { Field } from '/@/types/field';
import { getCrops } from '/@/views/dashboard/hooks/crops';

function FieldItem({
    field,
    fieldIsSelected,
    setSelectedField,
    dataTestId,
    showGrowerLabel = true,
    lastCampaign,
}: {
    field: Field;
    fieldIsSelected?: boolean;
    setSelectedField: (selected: Field) => void;
    dataTestId?: string;
    showGrowerLabel?: boolean;
    lastCampaign?: LastCampaign;
}) {
    const units = useUnitProvider();
    const userNodeQuery = useGetUserNode(field.userNode);
    const crops = getCrops();

    const lastCampaignLabel = lastCampaign
        ? [
              lastCampaign.plantingDate ? dayjs(lastCampaign.plantingDate).year() : undefined,
              [crops[lastCampaign.crop]?.name, lastCampaign.productName].filter(Boolean).join(' '),
              units.toYield(lastCampaign.averageYield, lastCampaign.crop, 0).toString(),
          ]
              .filter(Boolean)
              .join(' · ')
        : undefined;

    return (
        <Stack
            direction='row'
            alignItems='center'
            spacing={2}
            px={2}
            py={1.5}
            onClick={() => setSelectedField(field)}
            backgroundColor={fieldIsSelected ? '#F5F5F5' : 'white'}
            sx={{ cursor: 'pointer', borderBottom: '1px solid lightgray', width: '100%' }}
            data-testid={dataTestId}
        >
            <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FieldIcon coordinates={field.geometry} selected />
            </Box>
            <Grid container width='100%' alignItems='center'>
                <Grid item xs={12} sm={8}>
                    {showGrowerLabel && userNodeQuery.data?.name && (
                        <Typography
                            variant='overline'
                            color='primary.mui.inactive'
                            sx={{ flexGrow: 1, wordBreak: 'break-word' }}
                        >
                            {userNodeQuery.data.isSelf ? _('Mi cuenta') : userNodeQuery.data.name}
                        </Typography>
                    )}
                    <Typography
                        className='mp-mask'
                        variant='subtitle1'
                        fontWeight='bold'
                        color='primary'
                        sx={{ flexGrow: 1, wordBreak: 'break-word' }}
                    >
                        {field.name}
                    </Typography>
                    {lastCampaignLabel && (
                        <Typography
                            variant='caption'
                            color='primary.mui.inactive'
                            sx={{ flexGrow: 1, wordBreak: 'break-word' }}
                        >
                            {_('Última campaña: {{details}}', { details: lastCampaignLabel })}
                        </Typography>
                    )}
                </Grid>
                <Grid item xs={12} sm={4} sx={{ pl: 2 }}>
                    {field.recommendedCornYield && (
                        <Typography variant='body1' color='primary' sx={{ flexGrow: 1, wordBreak: 'break-word' }}>
                            {units.toYield(field.recommendedCornYield, 'corn').toString()}
                        </Typography>
                    )}
                </Grid>
            </Grid>

            <Radio checked={fieldIsSelected} value={field} color='secondary' size='medium' />
        </Stack>
    );
}

export default FieldItem;
